// ===================================================================
// PA GRANTS — Time Extension Request Tool
// AI-drafted letters, .docx & PDF generation, status tracking
// Two views: List (default) and Detail (row click)
// Role-aware: applicant vs grantee/admin vs viewer
// ===================================================================

import { useState, useEffect, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Textarea, Modal, Tabs, SimpleGrid, Checkbox, Loader, Center, Table, Alert, Divider,
  ActionIcon,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconPlus, IconEdit, IconTrash, IconSearch, IconDownload, IconClock,
  IconRobot, IconFileTypePdf, IconFileTypeDocx, IconCheck, IconX,
  IconArrowLeft, IconSend, IconArrowBack, IconSettings,
} from '@tabler/icons-react';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useAuthStore } from '../../store/authStore';
import { useRoleStore } from '../../store/roleStore';
import { useMyPATimeExtensions, useMyPAProjects } from '../../hooks/useMyPAOrgData';
import { callClaude, WORKER_URL, ORIGIN } from '../../lib/api-switch';
import { exportToCSV } from '../../lib/exportUtils';
import { TIME_EXTENSION_REASONS } from '../../types/pagrants';
import type { PATimeExtension } from '../../types/pagrants';
import { fmtDate } from '../../lib/date-format';

// ── Constants ──────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  draft: '#939598', submitted: 'var(--color-primary-700)', grantee_approved: 'var(--color-success)', forwarded_to_fema: 'var(--data-viz-100)',
  approved: '#03AC9E', denied: 'var(--color-error)', revision_needed: '#F86A0B',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', grantee_approved: 'Grantee Approved',
  forwarded_to_fema: 'Forwarded to FEMA', approved: 'FEMA Approved', denied: 'Denied',
  revision_needed: 'Revision Needed',
};

// Grantee can extend: Cat A/B +6 months, Cat C-G +30 months without FEMA approval
function getGranteeAuthorityMonths(category: string): number {
  if (!category) return 6;
  return ['A', 'B'].includes(category.toUpperCase()) ? 6 : 30;
}

const APPLICANT_SLUGS = ['pa_applicant_mgr', 'pa_applicant_staff', 'pa_applicant_viewer'];

// ── TE Recipients interface ───────────────────────────────────

interface TERecipient {
  name: string;
  title: string;
  org: string;
  address: string;
}

// ── Form type ──────────────────────────────────────────────────

interface TEForm {
  projectId: string; organizationId: string; disasterId: string;
  requestDate: string; currentDeadline: string;
  requestedNewDeadline: string; extensionMonths: string;
  recipientName: string; recipientTitle: string;
  recipientOrg: string; recipientAddress: string;
  justification: string; supportingCircumstances: string[];
  letterHeader: string; letterBody: string; letterClosing: string;
  status: string; notes: string;
}

const emptyForm = (): TEForm => ({
  projectId: '', organizationId: '', disasterId: '',
  requestDate: new Date().toISOString().split('T')[0], currentDeadline: '',
  requestedNewDeadline: '', extensionMonths: '6',
  recipientName: '', recipientTitle: '', recipientOrg: 'FEMA', recipientAddress: '',
  justification: '', supportingCircumstances: [],
  letterHeader: '', letterBody: '', letterClosing: '',
  status: 'draft', notes: '',
});

// ── Helpers ────────────────────────────────────────────────────

function addMonths(dateStr: string, months: number): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split('T')[0];
}

function parseLetter(content: string): { header: string; body: string; closing: string } {
  if (!content) return { header: '', body: '', closing: '' };
  const dec = (s: string) => s.replace(/\|\|NL\|\|/g, '\n');

  // New format with ||| markers and ||NL|| encoded newlines
  if (content.includes('|||HEADER|||') && content.includes('|||BODY|||')) {
    const headerStart = content.indexOf('|||HEADER|||') + '|||HEADER|||'.length;
    const bodyStart = content.indexOf('|||BODY|||');
    const closingStart = content.indexOf('|||CLOSING|||');
    const header = dec(content.substring(headerStart, bodyStart).trim());
    const body = closingStart > -1
      ? dec(content.substring(bodyStart + '|||BODY|||'.length, closingStart).trim())
      : dec(content.substring(bodyStart + '|||BODY|||'.length).trim());
    const closing = closingStart > -1
      ? dec(content.substring(closingStart + '|||CLOSING|||'.length).trim())
      : '';
    return { header, body, closing };
  }

  // Old format with === markers
  if (content.includes('===HEADER===') && content.includes('===BODY===')) {
    const headerStart = content.indexOf('===HEADER===') + '===HEADER==='.length;
    const bodyStart = content.indexOf('===BODY===');
    const closingStart = content.indexOf('===CLOSING===');
    const header = content.substring(headerStart, bodyStart).trim();
    const body = closingStart > -1
      ? content.substring(bodyStart + '===BODY==='.length, closingStart).trim()
      : content.substring(bodyStart + '===BODY==='.length).trim();
    const closing = closingStart > -1
      ? content.substring(closingStart + '===CLOSING==='.length).trim()
      : '';
    return { header, body, closing };
  }

  // Fallback: heuristic parsing for content without any markers
  let header = '', body = '', closing = '';
  let remaining = content;

  // Look for closing keywords (handle both real newlines and spaces before keyword)
  const closingMatch = remaining.match(/(?:\n|  )(Sincerely|Respectfully|Thank you|Best regards|Regards|Yours truly)[,\s]/i);
  if (closingMatch && closingMatch.index !== undefined) {
    closing = remaining.substring(closingMatch.index).trim();
    remaining = remaining.substring(0, closingMatch.index).trim();
  }

  // Look for RE:/Subject:/Dear as header boundary
  const reMatch = remaining.match(/(RE:|Subject:|Dear\s)/im);
  if (reMatch && reMatch.index !== undefined) {
    // Find the end of the RE line
    const reLineEnd = remaining.indexOf('\n', reMatch.index);
    const endIdx = reLineEnd > -1 ? reLineEnd : remaining.indexOf('  ', reMatch.index + reMatch[0].length);
    if (endIdx > -1) {
      header = remaining.substring(0, endIdx).trim();
      body = remaining.substring(endIdx).trim();
    } else {
      body = remaining.trim();
    }
  } else {
    body = remaining.trim();
  }

  return { header, body, closing };
}

/** Render letter as readable text (strips markers) */
function letterToReadable(content: string): string {
  if (!content) return '';
  if (content.includes('|||HEADER|||') || content.includes('===HEADER===')) {
    const s = parseLetter(content);
    return [s.header, '', s.body, '', s.closing].filter(v => v !== undefined).join('\n');
  }
  return content;
}

/** Extract submitter cert data from notes */
function parseSubCert(notes: string): { name: string; title: string; date: string } | null {
  const m = (notes || '').match(/\|\|\|SUBCERT\|\|\|([^|]*)\|\|\|([^|]*)\|\|\|([^|]*)/);
  if (!m) return null;
  return { name: m[1], title: m[2], date: m[3] };
}

/** Extract approver/grantee cert data from notes */
function parseApproverCert(notes: string): { name: string; title: string; date: string } | null {
  const m = (notes || '').match(/\|\|\|CERT\|\|\|([^|]*)\|\|\|([^|]*)\|\|\|([^|]*)/);
  if (!m) return null;
  return { name: m[1], title: m[2], date: m[3] };
}

/** Strip cert markers from notes for display */
function stripCertMarkers(notes: string): string {
  return (notes || '')
    .replace(/\n?\|\|\|CERT\|\|\|[^\n]*/, '')
    .replace(/\n?\|\|\|SUBCERT\|\|\|[^\n]*/, '')
    .trim();
}

// ── Component ──────────────────────────────────────────────────

export default function TimeExtensions() {
  const {
    timeExtensions, projects, organizations, disasters,
    loadingTimeExtensions, fetchTimeExtensions, fetchProjects, fetchOrganizations, fetchDisasters,
    createTimeExtension, updateTimeExtension, deleteTimeExtension, updateProject,
  } = usePAGrantsStore();
  const user = useAuthStore(s => s.user);
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const isApplicant = APPLICANT_SLUGS.includes(effectiveRole?.roleSlug ?? '');
  const isViewer = effectiveRole?.roleSlug === 'pa_applicant_viewer';

  // Data source: sub-applicants see org-filtered, grantees/admins see all
  const myTimeExtensions = useMyPATimeExtensions();
  const myProjects = useMyPAProjects();
  const sourceList = isApplicant ? myTimeExtensions : timeExtensions;
  const availableProjects = isApplicant ? myProjects : projects;

  // State
  const [selected, setSelected] = useState<PATimeExtension | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PATimeExtension | null>(null);
  const [form, setForm] = useState<TEForm>(emptyForm());
  const [activeTab, setActiveTab] = useState<string | null>('details');
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PATimeExtension | null>(null);

  // Review form state (for grantee/admin inline review)
  const [reviewDeadline, setReviewDeadline] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [approverName, setApproverName] = useState('');
  const [approverTitle, setApproverTitle] = useState('');
  const [certChecked, setCertChecked] = useState(false);
  // FEMA forwarding state
  const [fwdName, setFwdName] = useState('');
  const [fwdTitle, setFwdTitle] = useState('');
  const [fwdReason, setFwdReason] = useState('');
  const [fwdLetterBody, setFwdLetterBody] = useState('');
  const [fwdCertChecked, setFwdCertChecked] = useState(false);
  const [fwdDrafting, setFwdDrafting] = useState(false);
  const [fwdRecipientName, setFwdRecipientName] = useState('');
  const [fwdRecipientTitle, setFwdRecipientTitle] = useState('');
  const [fwdRecipientOrg, setFwdRecipientOrg] = useState('FEMA');
  const [fwdRecipientAddress, setFwdRecipientAddress] = useState('');

  // Submitter certification state
  const [showSubmitCert, setShowSubmitCert] = useState(false);
  const [submitterName, setSubmitterName] = useState('');
  const [submitterTitle, setSubmitterTitle] = useState('');
  const [submitCertChecked, setSubmitCertChecked] = useState(false);

  // TE Recipients (KV-stored presets)
  const [recipients, setRecipients] = useState<TERecipient[]>([]);
  const [showRecipientsEditor, setShowRecipientsEditor] = useState(false);
  const [recipientsEditing, setRecipientsEditing] = useState<TERecipient[]>([]);
  const [recipientsSaving, setRecipientsSaving] = useState(false);

  useEffect(() => { fetchTimeExtensions(); fetchProjects(); fetchOrganizations(); fetchDisasters(); }, []);

  // Fetch TE recipients from KV
  useEffect(() => {
    fetch(`${WORKER_URL}/api/config/te-recipients`, { headers: { 'Origin': ORIGIN } })
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (Array.isArray(data)) setRecipients(data); })
      .catch(() => {});
  }, []);

  // Keep selected item in sync with store data after actions
  useEffect(() => {
    if (selected) {
      const fresh = timeExtensions.find(t => t.Rid === selected.Rid);
      if (fresh) setSelected(fresh);
    }
  }, [timeExtensions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-compute approved deadline from current deadline + extension months
  useEffect(() => {
    if (!selected) return;
    const months = parseInt(selected.extensionMonths) || 0;
    const base = selected.currentDeadline;
    if (base && months > 0) {
      setReviewDeadline(addMonths(base, months));
    } else if (selected.requestedNewDeadline) {
      setReviewDeadline(selected.requestedNewDeadline);
    }
  }, [selected?.Rid, selected?.extensionMonths, selected?.currentDeadline]); // eslint-disable-line react-hooks/exhaustive-deps

  const orgMap = useMemo(() => new Map(organizations.map(o => [String(o.Rid), o])), [organizations]);
  const projMap = useMemo(() => new Map(projects.map(p => [String(p.Rid), p])), [projects]);
  const disasterMap = useMemo(() => new Map(disasters.map(d => [String(d.Rid), d])), [disasters]);

  const filtered = useMemo(() => {
    let list = sourceList;
    if (statusFilter) list = list.filter(t => t.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(t => {
        const proj = projMap.get(t.projectId);
        const org = orgMap.get(t.organizationId);
        return (proj?.projectNumber || '').toLowerCase().includes(q)
          || (proj?.projectTitle || '').toLowerCase().includes(q)
          || (org?.orgName || '').toLowerCase().includes(q)
          || (t.status || '').toLowerCase().includes(q);
      });
    }
    return list;
  }, [sourceList, statusFilter, search, projMap, orgMap]);

  // ── Recipients editor helpers ─────────────────────────────

  function openRecipientsEditor() {
    setRecipientsEditing(recipients.length > 0 ? recipients.map(r => ({ ...r })) : [{ name: '', title: '', org: '', address: '' }]);
    setShowRecipientsEditor(true);
  }

  async function saveRecipients() {
    setRecipientsSaving(true);
    try {
      const cleaned = recipientsEditing.filter(r => r.name.trim());
      await fetch(`${WORKER_URL}/api/config/te-recipients`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Origin': ORIGIN },
        body: JSON.stringify(cleaned),
      });
      setRecipients(cleaned);
      setShowRecipientsEditor(false);
    } catch (err) {
      console.error('Failed to save recipients:', err);
    } finally {
      setRecipientsSaving(false);
    }
  }

  // ── Modal helpers ──────────────────────────────────────────

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setActiveTab('details');
    setModalOpen(true);
  }

  function openEdit(te: PATimeExtension) {
    setEditing(te);
    const sections = parseLetter(te.letterContent);
    setForm({
      projectId: te.projectId, organizationId: te.organizationId, disasterId: te.disasterId,
      requestDate: te.requestDate, currentDeadline: te.currentDeadline,
      requestedNewDeadline: te.requestedNewDeadline, extensionMonths: te.extensionMonths,
      recipientName: te.recipientName, recipientTitle: te.recipientTitle,
      recipientOrg: te.recipientOrg, recipientAddress: te.recipientAddress,
      justification: te.justification,
      supportingCircumstances: te.supportingCircumstances ? te.supportingCircumstances.split(',') : [],
      letterHeader: sections.header, letterBody: sections.body, letterClosing: sections.closing,
      status: te.status, notes: te.notes,
    });
    setActiveTab('details');
    setModalOpen(true);
  }

  function handleProjectChange(projectId: string) {
    const proj = projMap.get(projectId);
    if (!proj) return;
    const disaster = disasterMap.get(proj.disasterId);
    const org = orgMap.get(proj.organizationId);
    const months = parseInt(form.extensionMonths) || 6;
    setForm(f => ({
      ...f,
      projectId,
      organizationId: proj.organizationId,
      disasterId: proj.disasterId,
      currentDeadline: proj.deadline || '',
      requestedNewDeadline: proj.deadline ? addMonths(proj.deadline, months) : '',
      // Only pre-fill recipient from disaster if user hasn't set one yet
      recipientName: f.recipientName || disaster?.programDeliveryManagerName || '',
      recipientTitle: f.recipientTitle || 'FEMA Program Delivery Manager',
      recipientOrg: f.recipientOrg || 'FEMA',
      recipientAddress: f.recipientAddress,
      letterHeader: buildHeader(org, disaster, proj, f),
      letterClosing: buildClosing(org),
    }));
  }

  function handleMonthsChange(months: string) {
    const m = parseInt(months) || 0;
    setForm(f => ({
      ...f,
      extensionMonths: months,
      requestedNewDeadline: f.currentDeadline ? addMonths(f.currentDeadline, m) : '',
    }));
  }

  function buildHeader(org: any, disaster: any, proj: any, f: TEForm): string {
    const date = f.requestDate || new Date().toISOString().split('T')[0];
    return `Date: ${date}\n\nFrom: ${org?.orgName || ''}\n${org?.physicalAddress || ''}\n${org?.physicalCity || ''}, ${org?.physicalState || ''} ${org?.physicalZip || ''}\n\nTo: ${f.recipientName || disaster?.programDeliveryManagerName || ''}\n${f.recipientTitle || 'FEMA Program Delivery Manager'}\n${f.recipientOrg || 'FEMA'}\n${f.recipientAddress || ''}\n\nRE: Time Extension Request — Project #${proj?.projectNumber || ''} (${proj?.projectTitle || ''})\nDisaster: ${disaster?.disasterNumber || ''} — ${disaster?.disasterName || ''}`;
  }

  function buildClosing(org: any): string {
    return `Sincerely,\n\n____________________________\n${org?.primaryContact || '[Authorized Representative]'}\n${org?.primaryTitle || ''}\n${org?.orgName || ''}\n${org?.primaryPhone || ''}\n${org?.primaryEmail || ''}`;
  }

  // ── AI Draft ───────────────────────────────────────────────

  async function handleDraftWithAI() {
    const proj = projMap.get(form.projectId);
    const disaster = disasterMap.get(form.disasterId);
    const org = orgMap.get(form.organizationId);
    if (!proj) return;

    setDrafting(true);
    try {
      const context = [
        `Project: #${proj.projectNumber} - ${proj.projectTitle}`,
        `Category: ${proj.category} (${proj.workType || 'repair'})`,
        `Disaster: ${disaster?.disasterNumber || ''} - ${disaster?.disasterName || ''}`,
        `Applicant: ${org?.orgName || ''}`,
        `Current Deadline: ${form.currentDeadline}`,
        `Requested New Deadline: ${form.requestedNewDeadline}`,
        `Extension: ${form.extensionMonths} months`,
        `Damage Description: ${proj.damageDescription || 'Not specified'}`,
        `Scope of Work: ${proj.scopeOfWork || 'Not specified'}`,
        `Estimated Cost: $${parseFloat(proj.estimatedCost || '0').toLocaleString()}`,
        `Completion: ${proj.completionPctEstimate || '0'}%`,
        `Work Start Date: ${proj.workStartDate || 'Not specified'}`,
        form.supportingCircumstances.length > 0
          ? `Supporting Circumstances:\n${form.supportingCircumstances.map(c => `- ${c}`).join('\n')}`
          : '',
        form.notes ? `Additional Context / Notes from Applicant:\n${form.notes}` : '',
      ].filter(Boolean).join('\n');

      const result = await callClaude(
        [{ role: 'user', content: `Draft the body of a formal FEMA PA time extension request letter. Be CONCISE and PRECISE — no filler.\n\nProject Context:\n${context}\n\nRules:\n- Start with one sentence stating the exact extension requested (from [current deadline] to [new deadline], ${form.extensionMonths} months)\n- Second paragraph: specific justification citing the circumstances and notes provided above. If applicant notes are provided, incorporate them directly.\n- Third paragraph: brief status of work completed (${proj.completionPctEstimate || '0'}% complete) and commitment to finish within the new timeframe\n- Maximum 3 short paragraphs. No greeting, date, addresses, or signature block.\n- Reference specific dollar amounts, dates, and project details from the context.\n- Do NOT add generic filler language or restate what FEMA already knows.` }],
        { system: 'You write concise, precise FEMA PA time extension letters. Every sentence must add information. No filler. Incorporate applicant notes directly. Cite specific project details.', maxTokens: 1000 },
      );

      setForm(f => ({
        ...f,
        letterBody: result,
        justification: result,
        letterHeader: f.letterHeader || buildHeader(org, disaster, proj, f),
        letterClosing: f.letterClosing || buildClosing(org),
      }));
      setActiveTab('letter');
    } catch (err) {
      console.error('AI draft failed:', err);
      notifications.show({
        title: 'Draft Failed',
        message: err instanceof Error ? err.message : 'Failed to draft letter. Check the console for details.',
        color: 'var(--color-error)',
        autoClose: 8000,
      });
    } finally {
      setDrafting(false);
    }
  }

  // ── Save ───────────────────────────────────────────────────

  function assembleLetterContent(): string {
    // Encode newlines as ||NL|| since Ignatius strips real newlines from TEXT fields
    const enc = (s: string) => s.replace(/\n/g, '||NL||');
    return `|||HEADER|||${enc(form.letterHeader)}|||BODY|||${enc(form.letterBody)}|||CLOSING|||${enc(form.letterClosing)}`;
  }

  async function handleSave() {
    if (!form.projectId) return;
    setSaving(true);
    try {
      const data = {
        projectId: form.projectId,
        organizationId: form.organizationId,
        disasterId: form.disasterId,
        requestDate: form.requestDate,
        currentDeadline: form.currentDeadline,
        requestedNewDeadline: form.requestedNewDeadline,
        extensionMonths: form.extensionMonths,
        recipientName: form.recipientName,
        recipientTitle: form.recipientTitle,
        recipientOrg: form.recipientOrg,
        recipientAddress: form.recipientAddress,
        justification: form.justification || form.letterBody,
        supportingCircumstances: form.supportingCircumstances.join(','),
        letterContent: assembleLetterContent(),
        status: form.status,
        createdBy: user?.fullName || user?.email || '',
        notes: form.notes,
      };

      if (editing) {
        await updateTimeExtension(editing.Rid, data);
      } else {
        await createTimeExtension(data);
      }
      setModalOpen(false);
      // If we were editing the selected detail item, let the useEffect sync it
    } finally { setSaving(false); }
  }

  // ── Status actions ─────────────────────────────────────────

  async function handleSubmitWithCert(te: PATimeExtension, name: string, title: string) {
    const certBlock = `|||SUBCERT|||${name}|||${title}|||${new Date().toISOString().split('T')[0]}`;
    // Preserve existing notes but strip any old SUBCERT
    const existingNotes = (te.notes || '').replace(/\n?\|\|\|SUBCERT\|\|\|[^\n]*/, '').trim();
    await updateTimeExtension(te.Rid, {
      status: 'submitted',
      notes: existingNotes ? `${existingNotes}\n${certBlock}` : certBlock,
    });
    setShowSubmitCert(false);
    setSubmitterName('');
    setSubmitterTitle('');
    setSubmitCertChecked(false);
  }

  async function handleRetract(te: PATimeExtension) {
    await updateTimeExtension(te.Rid, { status: 'draft' });
  }

  async function handleGranteeApprove(te: PATimeExtension, deadline: string, notes: string, name: string, title: string) {
    // Authority guard: if cumulative extensions for this project exceed the
    // grantee's authority under 44 CFR 206.204, block direct approval and
    // require the user to use "Forward to FEMA" instead. Uses the existing
    // getGranteeAuthorityMonths helper so logic stays in sync with the UI.
    const proj = projects.find(p => Number(p.Rid) === Number(te.projectId));
    const granteeAuthority = getGranteeAuthorityMonths(proj?.category || '');
    const priorMonths = timeExtensions
      .filter(t => t.Rid !== te.Rid && Number(t.projectId) === Number(te.projectId)
        && ['grantee_approved', 'approved'].includes(t.status))
      .reduce((sum, t) => sum + Number(t.extensionMonths || 0), 0);
    const cumulative = priorMonths + Number(te.extensionMonths || 0);
    if (cumulative > granteeAuthority) {
      notifications.show({
        title: 'Cannot approve under grantee authority',
        message: `Cumulative extensions (${cumulative} months) exceed your authority (${granteeAuthority} months for Category ${(proj?.category || '?').toUpperCase()}). This request must be forwarded to the FEMA Regional Administrator instead.`,
        color: 'var(--color-error)', autoClose: 8000,
      });
      return;
    }
    setReviewBusy(true);
    try {
      const approvedDeadline = deadline || te.requestedNewDeadline;
      const today = new Date().toISOString().split('T')[0];
      // Store approver name/title in notes with marker for decision letter generation
      const certBlock = `|||CERT|||${name}|||${title}|||${today}`;
      const fullNotes = notes ? `${notes}\n${certBlock}` : certBlock;
      await updateTimeExtension(te.Rid, {
        status: 'grantee_approved',
        approvalDate: today,
        approvedBy: name || user?.fullName || user?.email || '',
        approvedNewDeadline: approvedDeadline,
        notes: fullNotes,
      });
      await updateProject(Number(te.projectId), {
        extensionRequested: 'yes',
        extensionNewDeadline: approvedDeadline,
        extensionJustification: te.justification,
        deadline: approvedDeadline,
      });
      setReviewDeadline('');
      setReviewNotes('');
      setApproverName('');
      setApproverTitle('');
      setCertChecked(false);
    } finally { setReviewBusy(false); }
  }

  async function handleForwardToFema(te: PATimeExtension, notes: string, fwdReason: string, fwdName: string, fwdTitle: string, fwdLetterBody: string) {
    setReviewBusy(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const fwdBlock = `|||FWD|||${fwdName}|||${fwdTitle}|||${fwdReason}|||${today}`;
      const fwdRecipBlock = `|||FWDRECIP|||${fwdRecipientName}|||${fwdRecipientTitle}|||${fwdRecipientOrg}|||${fwdRecipientAddress}`;
      // Store forwarding letter body encoded like the request letter
      const fwdBodyEnc = fwdLetterBody.replace(/\n/g, '||NL||');
      const fullNotes = [notes, fwdBlock, fwdRecipBlock, `|||FWDBODY|||${fwdBodyEnc}`].filter(Boolean).join('\n');
      await updateTimeExtension(te.Rid, {
        status: 'forwarded_to_fema',
        approvalDate: today,
        approvedBy: fwdName || user?.fullName || user?.email || '',
        notes: fullNotes,
      });
      setReviewNotes('');
      setFwdName('');
      setFwdTitle('');
      setFwdReason('');
      setFwdLetterBody('');
      setFwdCertChecked(false);
    } finally { setReviewBusy(false); }
  }

  const FORWARDING_REASONS = [
    'Extension exceeds grantee authority timeframe',
    'Substantial change in scope of work',
    'Project complexity requires FEMA review',
    'Environmental/historic compliance delays',
    'Multiple prior extensions granted',
    'Disputed eligibility or cost issues',
    'Other — see notes',
  ];

  async function handleDraftFwdLetter() {
    if (!selected) return;
    const proj = projMap.get(selected.projectId);
    const disaster = disasterMap.get(selected.disasterId);
    const org = orgMap.get(selected.organizationId);
    const granteeOrg = org?.orgRole === 'grantee' ? org
      : organizations.find(o => String(o.Rid) === org?.parentOrgId && o.orgRole === 'grantee') || null;
    if (!proj) return;

    setFwdDrafting(true);
    try {
      const context = [
        `We (${granteeOrg?.orgName || 'Grantee'}) are forwarding a time extension request from ${org?.orgName || 'sub-applicant'}.`,
        `Project: #${proj.projectNumber} - ${proj.projectTitle}`,
        `Category: ${proj.category} (${proj.workType || 'repair'})`,
        `Disaster: ${disaster?.disasterNumber || ''} - ${disaster?.disasterName || ''}`,
        `Current Deadline: ${selected.currentDeadline}`,
        `Requested New Deadline: ${selected.requestedNewDeadline}`,
        `Extension Requested: ${selected.extensionMonths} months`,
        `Grantee Authority Limit: ${getGranteeAuthorityMonths(proj.category)} months for Category ${proj.category}`,
        `Reason for FEMA Referral: ${fwdReason || 'Extension exceeds grantee authority'}`,
        `Damage Description: ${proj.damageDescription || 'Not specified'}`,
        `Scope of Work: ${proj.scopeOfWork || 'Not specified'}`,
        `Estimated Cost: $${parseFloat(proj.estimatedCost || '0').toLocaleString()}`,
        `Completion: ${proj.completionPctEstimate || '0'}%`,
        selected.justification ? `Applicant's Justification Summary: ${selected.justification.substring(0, 500)}` : '',
        reviewNotes ? `Additional Grantee Notes: ${reviewNotes}` : '',
      ].filter(Boolean).join('\n');

      const result = await callClaude(
        [{ role: 'user', content: `Draft a formal letter from a FEMA PA grantee to the FEMA Regional Administrator forwarding a sub-applicant's time extension request for review and approval. Use this context:\n\n${context}\n\nWrite 2-3 paragraphs. Start by stating you are forwarding the attached time extension request and why it requires FEMA approval. Explain the reason for referral. Include your recommendation (recommend approval). Reference the applicant's justification briefly. End by offering to provide additional information. Do NOT include date, addresses, greeting, or signature — only the body paragraphs.` }],
        { system: 'You are an experienced FEMA Public Assistance grant management specialist writing on behalf of a state/county grantee to the FEMA Regional Administrator. Be formal, cite specific project details and regulatory references.', maxTokens: 1500 },
      );
      setFwdLetterBody(result);
    } catch (err) {
      console.error('AI draft failed:', err);
    } finally { setFwdDrafting(false); }
  }

  async function downloadFemaPackageDocx(te: PATimeExtension) {
    const { Document, Packer, Paragraph, TextRun, PageBreak } = await import('docx');
    const proj = projMap.get(te.projectId);
    const org = orgMap.get(te.organizationId);
    const disaster = disasterMap.get(te.disasterId);
    const granteeOrg = org?.orgRole === 'grantee' ? org
      : organizations.find(o => String(o.Rid) === org?.parentOrgId && o.orgRole === 'grantee') || null;

    // Parse forwarding data from notes
    const fwdMatch = (te.notes || '').match(/\|\|\|FWD\|\|\|([^|]*)\|\|\|([^|]*)\|\|\|([^|]*)\|\|\|([^|]*)/);
    const fName = fwdMatch?.[1] || te.approvedBy || '';
    const fTitle = fwdMatch?.[2] || '';
    const fReason = fwdMatch?.[3] || '';
    const fDate = fwdMatch?.[4] || te.approvalDate || '';
    const fBodyMatch = (te.notes || '').match(/\|\|\|FWDBODY\|\|\|([\s\S]*?)(?:\n\|\|\||$)/);
    const fBody = fBodyMatch?.[1]?.replace(/\|\|NL\|\|/g, '\n').trim() || '';

    const FONT = 'Calibri';
    const SZ = 22;
    const SZ_SM = 20;
    const children: InstanceType<typeof Paragraph>[] = [];
    const textP = (text: string, opts?: { bold?: boolean; size?: number; spacing?: number; italic?: boolean }) =>
      new Paragraph({ children: [new TextRun({ text, font: FONT, size: opts?.size ?? SZ, bold: opts?.bold, italics: opts?.italic })], spacing: { after: opts?.spacing ?? 40 } });
    const blank = () => new Paragraph({ children: [], spacing: { after: 200 } });

    // ── Page 1: Forwarding Cover Letter ──
    children.push(textP(`Date: ${fDate}`, { size: SZ_SM, bold: true, spacing: 80 }));
    children.push(blank());
    children.push(textP('From:', { bold: true, size: SZ_SM, spacing: 20 }));
    children.push(textP(`${fName}${fTitle ? ', ' + fTitle : ''}`, { size: SZ_SM, spacing: 20 }));
    children.push(textP(granteeOrg?.orgName || '', { size: SZ_SM, spacing: 20 }));
    const gAddr = [granteeOrg?.physicalAddress, [granteeOrg?.physicalCity, granteeOrg?.physicalState, granteeOrg?.physicalZip].filter(Boolean).join(', ')].filter(Boolean).join(', ');
    if (gAddr) children.push(textP(gAddr, { size: SZ_SM, spacing: 20 }));
    children.push(blank());

    // To: FEMA — use stored forwarding recipient
    const fwdRecipMatch = (te.notes || '').match(/\|\|\|FWDRECIP\|\|\|([^|]*)\|\|\|([^|]*)\|\|\|([^|]*)\|\|\|([^|]*)/);
    const fwdToName = fwdRecipMatch?.[1] || disaster?.programDeliveryManagerName || te.recipientName || 'FEMA Regional Administrator';
    const fwdToTitle = fwdRecipMatch?.[2] || '';
    const fwdToOrg = fwdRecipMatch?.[3] || 'FEMA';
    const fwdToAddr = fwdRecipMatch?.[4] || te.recipientAddress || '';

    children.push(textP('To:', { bold: true, size: SZ_SM, spacing: 20 }));
    children.push(textP(fwdToName, { size: SZ_SM, spacing: 20 }));
    if (fwdToTitle) children.push(textP(fwdToTitle, { size: SZ_SM, spacing: 20 }));
    children.push(textP(fwdToOrg, { size: SZ_SM, spacing: 20 }));
    if (fwdToAddr) children.push(textP(fwdToAddr, { size: SZ_SM, spacing: 20 }));
    children.push(blank());

    children.push(textP(`RE: Forwarding of Time Extension Request — Project #${proj?.projectNumber || ''} (${proj?.projectTitle || ''})`, { bold: true, size: SZ, spacing: 40 }));
    children.push(textP(`Disaster: ${disaster?.disasterNumber || ''} — ${disaster?.disasterName || ''}`, { size: SZ_SM, spacing: 20 }));
    children.push(textP(`Applicant: ${org?.orgName || ''}`, { size: SZ_SM, spacing: 20 }));
    children.push(textP(`Reason for Referral: ${fReason}`, { size: SZ_SM, spacing: 20 }));
    children.push(blank());

    // Body
    if (fBody) {
      for (const para of fBody.split(/\n\n+/)) {
        const cleaned = para.replace(/\n/g, ' ').trim();
        if (cleaned) children.push(new Paragraph({ children: [new TextRun({ text: cleaned, font: FONT, size: SZ })], spacing: { after: 200, line: 276 } }));
      }
    } else {
      children.push(new Paragraph({ children: [new TextRun({ text: `We are forwarding the attached time extension request from ${org?.orgName || 'the applicant'} for your review and approval. The requested extension of ${te.extensionMonths} months exceeds the grantee's authority under 44 CFR 206.204.`, font: FONT, size: SZ })], spacing: { after: 200, line: 276 } }));
    }
    children.push(blank());

    // Signature
    children.push(textP('Sincerely,', { italic: true, spacing: 80 }));
    children.push(blank());
    children.push(textP(`/s/ ${fName}`, { bold: true, spacing: 40 }));
    if (fTitle) children.push(textP(fTitle, { size: SZ_SM, spacing: 20 }));
    children.push(textP(granteeOrg?.orgName || '', { size: SZ_SM, spacing: 20 }));

    // ── Page Break + Page 2: Original Request Letter ──
    const reqSections = getSections(te);
    if (reqSections.header || reqSections.body || reqSections.closing) {
      children.push(new Paragraph({ children: [new PageBreak()] }));
      children.push(textP('ATTACHMENT: Original Time Extension Request Letter', { bold: true, size: SZ, spacing: 120 }));
      children.push(blank());

      // Re-render the request letter on page 2
      if (reqSections.header) {
        for (const line of reqSections.header.split('\n')) {
          const t = line.trim();
          if (!t) { children.push(blank()); continue; }
          children.push(textP(t, { size: SZ_SM, spacing: /^(Date|From|To|RE):/i.test(t) ? 80 : 20, bold: /^(Date|From|To|RE):/i.test(t) }));
        }
        children.push(blank());
      }
      if (reqSections.body) {
        for (const para of reqSections.body.split(/\n\n+/)) {
          const cleaned = para.replace(/\n/g, ' ').trim();
          if (cleaned) children.push(new Paragraph({ children: [new TextRun({ text: cleaned, font: FONT, size: SZ })], spacing: { after: 200, line: 276 } }));
        }
        children.push(blank());
      }
      if (reqSections.closing) {
        const parts = reqSections.closing.includes('\n') ? reqSections.closing.split('\n') : reqSections.closing.split(/  +/);
        for (const p of parts) {
          const t = p.trim();
          if (!t) { children.push(blank()); continue; }
          children.push(textP(t, { size: SZ, spacing: t.startsWith('____') ? 200 : 20, italic: /^(Sincerely|Respectfully)/i.test(t) }));
        }
      }
    }

    const doc = new Document({ sections: [{ properties: { page: { margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } } }, children }] });
    const blob = await Packer.toBlob(doc);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `FEMA-TE-Package-${proj?.projectNumber || te.Rid}-${fDate || 'fwd'}.docx`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function downloadFemaPackagePdf(te: PATimeExtension) {
    const { default: jsPDF } = await import('jspdf');
    const pdfDoc = new jsPDF();
    const proj = projMap.get(te.projectId);
    const org = orgMap.get(te.organizationId);
    const disaster = disasterMap.get(te.disasterId);
    const granteeOrg = org?.orgRole === 'grantee' ? org
      : organizations.find(o => String(o.Rid) === org?.parentOrgId && o.orgRole === 'grantee') || null;

    const fwdMatch = (te.notes || '').match(/\|\|\|FWD\|\|\|([^|]*)\|\|\|([^|]*)\|\|\|([^|]*)\|\|\|([^|]*)/);
    const fName = fwdMatch?.[1] || te.approvedBy || '';
    const fTitle = fwdMatch?.[2] || '';
    const fReason = fwdMatch?.[3] || '';
    const fDate = fwdMatch?.[4] || te.approvalDate || '';
    const fBodyMatch = (te.notes || '').match(/\|\|\|FWDBODY\|\|\|([\s\S]*?)(?:\n\|\|\||$)/);
    const fBody = fBodyMatch?.[1]?.replace(/\|\|NL\|\|/g, '\n').trim() || '';

    const LEFT = 25;
    const PW = 160;
    let y = 30;
    function checkPage(n: number) { if (y + n > 270) { pdfDoc.addPage(); y = 30; } }
    function draw(text: string, opts?: { bold?: boolean; size?: number; italic?: boolean }) {
      const sz = opts?.size ?? 11;
      pdfDoc.setFontSize(sz);
      pdfDoc.setFont('helvetica', opts?.bold ? 'bold' : opts?.italic ? 'italic' : 'normal');
      for (const wl of pdfDoc.splitTextToSize(text, PW)) { checkPage(sz * 0.5); pdfDoc.text(wl, LEFT, y); y += sz * 0.45; }
    }
    function gap(h = 4) { y += h; }

    // ── Page 1: Forwarding Letter ──
    draw(`Date: ${fDate}`, { bold: true, size: 10 }); gap(6);
    draw('From:', { bold: true, size: 10 });
    draw(`${fName}${fTitle ? ', ' + fTitle : ''}`, { size: 10 });
    draw(granteeOrg?.orgName || '', { size: 10 });
    const gAddr = [granteeOrg?.physicalAddress, [granteeOrg?.physicalCity, granteeOrg?.physicalState, granteeOrg?.physicalZip].filter(Boolean).join(', ')].filter(Boolean).join(', ');
    if (gAddr) draw(gAddr, { size: 10 });
    gap(6);
    const fwdRecipMatch = (te.notes || '').match(/\|\|\|FWDRECIP\|\|\|([^|]*)\|\|\|([^|]*)\|\|\|([^|]*)\|\|\|([^|]*)/);
    const fwdToName = fwdRecipMatch?.[1] || disaster?.programDeliveryManagerName || te.recipientName || 'FEMA Regional Administrator';
    const fwdToTitle = fwdRecipMatch?.[2] || '';
    const fwdToOrg = fwdRecipMatch?.[3] || 'FEMA';
    const fwdToAddr = fwdRecipMatch?.[4] || te.recipientAddress || '';

    draw('To:', { bold: true, size: 10 });
    draw(fwdToName, { size: 10 });
    if (fwdToTitle) draw(fwdToTitle, { size: 10 });
    draw(fwdToOrg, { size: 10 });
    if (fwdToAddr) draw(fwdToAddr, { size: 10 });
    gap(6);
    draw(`RE: Forwarding of Time Extension Request — Project #${proj?.projectNumber || ''} (${proj?.projectTitle || ''})`, { bold: true, size: 11 });
    draw(`Disaster: ${disaster?.disasterNumber || ''} — ${disaster?.disasterName || ''}`, { size: 10 });
    draw(`Applicant: ${org?.orgName || ''}`, { size: 10 });
    draw(`Reason for Referral: ${fReason}`, { size: 10 });
    gap(6);

    if (fBody) {
      for (const para of fBody.split(/\n\n+/)) {
        const cleaned = para.replace(/\n/g, ' ').trim();
        if (cleaned) { draw(cleaned, { size: 11 }); gap(5); }
      }
    } else {
      draw(`We are forwarding the attached time extension request from ${org?.orgName || 'the applicant'} for your review and approval. The requested extension of ${te.extensionMonths} months exceeds the grantee's authority under 44 CFR 206.204.`, { size: 11 });
      gap(5);
    }
    gap(4);
    draw('Sincerely,', { italic: true }); gap(6);
    draw(`/s/ ${fName}`, { bold: true });
    if (fTitle) draw(fTitle, { size: 10 });
    draw(granteeOrg?.orgName || '', { size: 10 });

    // ── Page 2: Original Request Letter ──
    pdfDoc.addPage(); y = 30;
    draw('ATTACHMENT: Original Time Extension Request Letter', { bold: true, size: 12 }); gap(8);
    const reqSections = getSections(te);
    if (reqSections.header) {
      for (const line of reqSections.header.split('\n')) {
        const t = line.trim();
        if (!t) { gap(3); continue; }
        draw(t, { bold: /^(Date|From|To|RE):/i.test(t), size: 10 });
      }
      gap(6);
    }
    if (reqSections.body) {
      for (const para of reqSections.body.split(/\n\n+/)) {
        const cleaned = para.replace(/\n/g, ' ').trim();
        if (cleaned) { draw(cleaned, { size: 11 }); gap(5); }
      }
      gap(4);
    }
    if (reqSections.closing) {
      const parts = reqSections.closing.includes('\n') ? reqSections.closing.split('\n') : reqSections.closing.split(/  +/);
      for (const p of parts) {
        const t = p.trim();
        if (!t) { gap(3); continue; }
        if (t.startsWith('____')) gap(8);
        draw(t, { size: 11 });
      }
    }

    pdfDoc.save(`FEMA-TE-Package-${proj?.projectNumber || te.Rid}-${fDate || 'fwd'}.pdf`);
  }

  async function handleFemaApprove(te: PATimeExtension, deadline: string, notes: string) {
    setReviewBusy(true);
    try {
      const approvedDeadline = deadline || te.requestedNewDeadline;
      await updateTimeExtension(te.Rid, {
        status: 'approved',
        approvalDate: new Date().toISOString().split('T')[0],
        approvedBy: user?.fullName || user?.email || '',
        approvedNewDeadline: approvedDeadline,
        notes,
      });
      await updateProject(Number(te.projectId), {
        extensionRequested: 'yes',
        extensionNewDeadline: approvedDeadline,
        extensionJustification: te.justification,
        deadline: approvedDeadline,
      });
      setReviewDeadline('');
      setReviewNotes('');
    } finally { setReviewBusy(false); }
  }

  async function handleRequestRevision(te: PATimeExtension, notes: string) {
    setReviewBusy(true);
    try {
      await updateTimeExtension(te.Rid, {
        status: 'revision_needed',
        approvalDate: new Date().toISOString().split('T')[0],
        approvedBy: user?.fullName || user?.email || '',
        notes,
      });
      setReviewDeadline('');
      setReviewNotes('');
    } finally { setReviewBusy(false); }
  }

  async function handleDeny(te: PATimeExtension, notes: string) {
    setReviewBusy(true);
    try {
      await updateTimeExtension(te.Rid, {
        status: 'denied',
        approvalDate: new Date().toISOString().split('T')[0],
        approvedBy: user?.fullName || user?.email || '',
        notes,
      });
      setReviewDeadline('');
      setReviewNotes('');
    } finally { setReviewBusy(false); }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await deleteTimeExtension(deleteTarget.Rid);
    setDeleteTarget(null);
    if (selected && selected.Rid === deleteTarget.Rid) setSelected(null);
  }

  // ── Letter section helpers ─────────────────────────────────

  function getSections(te: PATimeExtension): { header: string; body: string; closing: string } {
    if (editing && editing.Rid === te.Rid && (form.letterHeader || form.letterBody || form.letterClosing)) {
      return { header: form.letterHeader, body: form.letterBody, closing: form.letterClosing };
    }
    const content = te.letterContent || '';
    if (!content) return { header: '', body: '', closing: '' };
    return parseLetter(content);
  }

  // ── Request Letter Docx download (uses submitter signature) ──

  async function downloadDocx(te: PATimeExtension) {
    const { Document, Packer, Paragraph, TextRun } = await import('docx');
    let sections = getSections(te);
    // Extract SUBMITTER certification data for request letter signature
    const subCert = parseSubCert(te.notes || '');
    const sigName = subCert?.name || '';
    const sigTitle = subCert?.title || '';
    const sigDate = subCert?.date || '';
    if (!sections.header && !sections.body && !sections.closing) {
      sections = { header: '', body: te.letterContent || te.justification || '', closing: '' };
    }
    // Auto-generate missing header/closing from project data
    if (!sections.header) {
      const proj = projMap.get(te.projectId);
      const disaster = proj ? disasterMap.get(proj.disasterId) : undefined;
      const org = proj ? orgMap.get(te.organizationId) : undefined;
      if (proj) sections.header = buildHeader(org, disaster, proj, {
        requestDate: te.requestDate,
        recipientName: te.recipientName,
        recipientTitle: te.recipientTitle,
        recipientOrg: te.recipientOrg,
        recipientAddress: te.recipientAddress,
      } as TEForm);
    }
    if (!sections.closing) {
      const org = orgMap.get(te.organizationId);
      if (org) sections.closing = buildClosing(org);
    }
    const children: InstanceType<typeof Paragraph>[] = [];
    const FONT = 'Calibri';
    const SZ_NORMAL = 22;
    const SZ_SMALL = 20;

    const textP = (text: string, opts?: { bold?: boolean; size?: number; spacing?: number; italic?: boolean }) =>
      new Paragraph({
        children: [new TextRun({ text, font: FONT, size: opts?.size ?? SZ_NORMAL, bold: opts?.bold, italics: opts?.italic })],
        spacing: { after: opts?.spacing ?? 40 },
      });

    const blankLine = () => new Paragraph({ children: [], spacing: { after: 200 } });

    if (sections.header) {
      for (const line of sections.header.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) { children.push(blankLine()); continue; }
        const isLabel = /^(Date|From|To|RE):/i.test(trimmed);
        const isRE = /^RE:/i.test(trimmed);
        if (isRE) {
          children.push(blankLine());
          children.push(textP(trimmed, { bold: true, size: SZ_NORMAL }));
        } else {
          children.push(textP(trimmed, { bold: isLabel, size: SZ_SMALL, spacing: isLabel ? 80 : 20 }));
        }
      }
      children.push(blankLine());
    }

    if (sections.body) {
      const bodyParagraphs = sections.body.split(/\n\n+/);
      for (const para of bodyParagraphs) {
        const cleaned = para.replace(/\n/g, ' ').trim();
        if (!cleaned) continue;
        children.push(new Paragraph({
          children: [new TextRun({ text: cleaned, font: FONT, size: SZ_NORMAL })],
          spacing: { after: 200, line: 276 },
        }));
      }
      children.push(blankLine());
    }

    if (sections.closing) {
      const closingText = sections.closing;
      const underscoreIdx = closingText.indexOf('____');

      if (underscoreIdx > -1 && sigName) {
        const beforeSign = closingText.substring(0, underscoreIdx).trim();
        const afterUnderscores = closingText.substring(underscoreIdx).replace(/^_+/, '').trim();

        if (beforeSign) {
          for (const part of beforeSign.split(/\n/).filter(p => p.trim())) {
            children.push(textP(part.trim(), {
              size: SZ_NORMAL,
              italic: /^(Sincerely|Respectfully|Thank you)/i.test(part.trim()),
              spacing: 80,
            }));
          }
        }

        children.push(blankLine());
        children.push(textP(`/s/ ${sigName}`, { bold: true, size: SZ_NORMAL, spacing: 40 }));
        if (sigTitle) children.push(textP(sigTitle, { size: SZ_SMALL, spacing: 20 }));
        if (sigDate) children.push(textP(`Date: ${sigDate}`, { size: SZ_SMALL, spacing: 20 }));
        children.push(blankLine());

        if (afterUnderscores) {
          for (const part of afterUnderscores.split(/\n|  +/).filter(p => p.trim())) {
            children.push(textP(part.trim(), { size: SZ_SMALL, spacing: 20 }));
          }
        }
      } else {
        const parts = closingText.includes('\n')
          ? closingText.split('\n')
          : closingText.split(/  +/);
        for (const part of parts) {
          const trimmed = part.trim();
          if (!trimmed) { children.push(blankLine()); continue; }
          children.push(textP(trimmed, {
            size: SZ_NORMAL,
            spacing: trimmed.startsWith('____') ? 200 : 20,
            italic: /^(Sincerely|Respectfully|Thank you)/i.test(trimmed),
          }));
        }
      }
    }

    const doc = new Document({
      sections: [{
        properties: { page: { margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } } },
        children,
      }],
    });

    const blob = await Packer.toBlob(doc);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const proj = projMap.get(te.projectId);
    a.download = `TE-Request-${proj?.projectNumber || te.Rid}-${te.requestDate || 'draft'}.docx`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // ── Request Letter PDF download (uses submitter signature) ──

  async function downloadPdf(te: PATimeExtension) {
    const { default: jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    let sections = getSections(te);
    if (!sections.header && !sections.body && !sections.closing) {
      sections = { header: '', body: te.letterContent || te.justification || '', closing: '' };
    }
    // Auto-generate missing header/closing from project data
    if (!sections.header) {
      const proj = projMap.get(te.projectId);
      const disaster = proj ? disasterMap.get(proj.disasterId) : undefined;
      const org = proj ? orgMap.get(te.organizationId) : undefined;
      if (proj) sections.header = buildHeader(org, disaster, proj, {
        requestDate: te.requestDate,
        recipientName: te.recipientName,
        recipientTitle: te.recipientTitle,
        recipientOrg: te.recipientOrg,
        recipientAddress: te.recipientAddress,
      } as TEForm);
    }
    if (!sections.closing) {
      const org = orgMap.get(te.organizationId);
      if (org) sections.closing = buildClosing(org);
    }
    // Extract SUBMITTER certification data
    const subCert = parseSubCert(te.notes || '');
    const sigName = subCert?.name || '';
    const sigTitle = subCert?.title || '';
    const sigDate = subCert?.date || '';

    const LEFT = 25;
    const RIGHT_MARGIN = 25;
    const PAGE_WIDTH = 210 - LEFT - RIGHT_MARGIN;
    let y = 30;

    function checkPage(needed: number) {
      if (y + needed > 270) { doc.addPage(); y = 30; }
    }

    function drawLine(text: string, opts?: { bold?: boolean; size?: number; italic?: boolean }) {
      const sz = opts?.size ?? 11;
      doc.setFontSize(sz);
      doc.setFont('helvetica', opts?.bold ? 'bold' : opts?.italic ? 'italic' : 'normal');
      const wrapped = doc.splitTextToSize(text, PAGE_WIDTH);
      for (const wl of wrapped) {
        checkPage(sz * 0.5);
        doc.text(wl, LEFT, y);
        y += sz * 0.45;
      }
    }

    function blankLine(height = 4) { y += height; }

    if (sections.header) {
      for (const line of sections.header.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) { blankLine(3); continue; }
        const isLabel = /^(Date|From|To):/i.test(trimmed);
        const isRE = /^RE:/i.test(trimmed);
        if (isRE) {
          blankLine(3);
          drawLine(trimmed, { bold: true, size: 11 });
        } else {
          drawLine(trimmed, { bold: isLabel, size: 10 });
        }
      }
      blankLine(6);
    }

    if (sections.body) {
      const bodyParagraphs = sections.body.split(/\n\n+/);
      for (const para of bodyParagraphs) {
        const cleaned = para.replace(/\n/g, ' ').trim();
        if (!cleaned) continue;
        drawLine(cleaned, { size: 11 });
        blankLine(5);
      }
      blankLine(4);
    }

    if (sections.closing) {
      const closingText = sections.closing;
      const uIdx = closingText.indexOf('____');

      if (uIdx > -1 && sigName) {
        const beforeSign = closingText.substring(0, uIdx).trim();
        const afterUnderscores = closingText.substring(uIdx).replace(/^_+/, '').trim();

        if (beforeSign) {
          for (const part of beforeSign.split(/\n/).filter(p => p.trim())) {
            drawLine(part.trim(), { size: 11 });
            if (/^(Sincerely|Respectfully|Thank you)/i.test(part.trim())) blankLine(2);
          }
        }

        blankLine(6);
        drawLine(`/s/ ${sigName}`, { bold: true, size: 11 });
        if (sigTitle) drawLine(sigTitle, { size: 10 });
        if (sigDate) drawLine(`Date: ${sigDate}`, { size: 10 });
        blankLine(3);

        if (afterUnderscores) {
          for (const part of afterUnderscores.split(/\n|  +/).filter(p => p.trim())) {
            drawLine(part.trim(), { size: 10 });
          }
        }
      } else {
        const parts = closingText.includes('\n')
          ? closingText.split('\n')
          : closingText.split(/  +/);
        for (const part of parts) {
          const trimmed = part.trim();
          if (!trimmed) { blankLine(3); continue; }
          if (trimmed.startsWith('____')) blankLine(8);
          drawLine(trimmed, { size: 11 });
          if (/^(Sincerely|Respectfully|Thank you)/i.test(trimmed)) blankLine(2);
        }
      }
    }

    const proj = projMap.get(te.projectId);
    doc.save(`TE-Request-${proj?.projectNumber || te.Rid}-${te.requestDate || 'draft'}.pdf`);
  }

  // ── Decision Letter Docx download ─────────────────────────

  async function downloadDecisionDocx(te: PATimeExtension) {
    const { Document, Packer, Paragraph, TextRun } = await import('docx');
    const proj = projMap.get(te.projectId);
    const org = orgMap.get(te.organizationId);
    const disaster = disasterMap.get(te.disasterId);
    const approverCert = parseApproverCert(te.notes || '');
    const aName = approverCert?.name || te.approvedBy || '';
    const aTitle = approverCert?.title || '';
    const aDate = approverCert?.date || te.approvalDate || '';
    const isApproval = te.status === 'grantee_approved' || te.status === 'approved';
    const displayNotes = stripCertMarkers(te.notes);

    const FONT = 'Calibri';
    const SZ_NORMAL = 22;
    const SZ_SMALL = 20;
    const children: InstanceType<typeof Paragraph>[] = [];

    const textP = (text: string, opts?: { bold?: boolean; size?: number; spacing?: number; italic?: boolean }) =>
      new Paragraph({
        children: [new TextRun({ text, font: FONT, size: opts?.size ?? SZ_NORMAL, bold: opts?.bold, italics: opts?.italic })],
        spacing: { after: opts?.spacing ?? 40 },
      });
    const blankLine = () => new Paragraph({ children: [], spacing: { after: 200 } });

    // Date
    children.push(textP(`Date: ${aDate}`, { size: SZ_SMALL, spacing: 80 }));
    children.push(blankLine());

    // From — find the grantee org (parent of the applicant, or the applicant if it is the grantee)
    const granteeOrg = org?.orgRole === 'grantee' ? org
      : organizations.find(o => String(o.Rid) === org?.parentOrgId && o.orgRole === 'grantee') || null;
    const granteeOrgName = granteeOrg?.orgName || 'Grantee';
    const granteeAddr = [granteeOrg?.physicalAddress, [granteeOrg?.physicalCity, granteeOrg?.physicalState, granteeOrg?.physicalZip].filter(Boolean).join(', ')].filter(Boolean).join(', ');

    children.push(textP('From:', { bold: true, size: SZ_SMALL, spacing: 20 }));
    children.push(textP(`${aName}${aTitle ? ', ' + aTitle : ''}`, { size: SZ_SMALL, spacing: 20 }));
    children.push(textP(granteeOrgName, { size: SZ_SMALL, spacing: 20 }));
    if (granteeAddr) children.push(textP(granteeAddr, { size: SZ_SMALL, spacing: 20 }));
    children.push(blankLine());

    // To
    children.push(textP('To:', { bold: true, size: SZ_SMALL, spacing: 20 }));
    children.push(textP(org?.primaryContact || org?.orgName || '', { size: SZ_SMALL, spacing: 20 }));
    children.push(textP(org?.orgName || '', { size: SZ_SMALL, spacing: 20 }));
    const orgAddr = [org?.physicalAddress, [org?.physicalCity, org?.physicalState, org?.physicalZip].filter(Boolean).join(', ')].filter(Boolean).join(', ');
    if (orgAddr) children.push(textP(orgAddr, { size: SZ_SMALL, spacing: 20 }));
    children.push(blankLine());

    // RE line
    const statusWord = isApproval ? 'Approval' : 'Denial';
    children.push(textP(`RE: Time Extension ${statusWord} — Project #${proj?.projectNumber || ''} (${proj?.projectTitle || ''})`, { bold: true, size: SZ_NORMAL, spacing: 40 }));
    children.push(textP(`Disaster: ${disaster?.disasterNumber || ''} — ${disaster?.disasterName || ''}`, { size: SZ_SMALL, spacing: 20 }));
    children.push(blankLine());

    // Body
    if (isApproval) {
      children.push(new Paragraph({
        children: [new TextRun({ text: 'This letter serves to confirm that your request for a time extension for the above-referenced project has been approved under the grantee\'s authority per 44 CFR 206.204.', font: FONT, size: SZ_NORMAL })],
        spacing: { after: 200, line: 276 },
      }));
      children.push(new Paragraph({
        children: [new TextRun({ text: `The new completion deadline is ${te.approvedNewDeadline || te.requestedNewDeadline || '—'}. The original deadline was ${te.currentDeadline || '—'}, and an extension of ${te.extensionMonths || '—'} months has been granted.`, font: FONT, size: SZ_NORMAL })],
        spacing: { after: 200, line: 276 },
      }));
    } else {
      children.push(new Paragraph({
        children: [new TextRun({ text: 'After careful review, your request for a time extension for the above-referenced project has been denied.', font: FONT, size: SZ_NORMAL })],
        spacing: { after: 200, line: 276 },
      }));
    }

    if (displayNotes) {
      children.push(blankLine());
      children.push(new Paragraph({
        children: [new TextRun({ text: displayNotes, font: FONT, size: SZ_NORMAL })],
        spacing: { after: 200, line: 276 },
      }));
    }

    children.push(blankLine());
    children.push(new Paragraph({
      children: [new TextRun({ text: 'Please contact this office if you have questions regarding this decision.', font: FONT, size: SZ_NORMAL })],
      spacing: { after: 200, line: 276 },
    }));
    children.push(blankLine());

    // Signature
    children.push(textP('Sincerely,', { italic: true, size: SZ_NORMAL, spacing: 80 }));
    children.push(blankLine());
    children.push(textP(`/s/ ${aName}`, { bold: true, size: SZ_NORMAL, spacing: 40 }));
    if (aTitle) children.push(textP(aTitle, { size: SZ_SMALL, spacing: 20 }));
    children.push(textP(granteeOrgName, { size: SZ_SMALL, spacing: 20 }));
    children.push(blankLine());
    children.push(blankLine());

    // Certification
    children.push(new Paragraph({
      children: [new TextRun({ text: 'CERTIFICATION: I hereby certify that this decision has been made in accordance with 44 CFR 206.204.', font: FONT, size: SZ_SMALL, italics: true })],
      spacing: { after: 80 },
    }));

    const doc = new Document({
      sections: [{
        properties: { page: { margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } } },
        children,
      }],
    });

    const blob = await Packer.toBlob(doc);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `TE-Decision-${proj?.projectNumber || te.Rid}-${aDate || 'decision'}.docx`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // ── Decision Letter PDF download ──────────────────────────

  async function downloadDecisionPdf(te: PATimeExtension) {
    const { default: jsPDF } = await import('jspdf');
    const pdfDoc = new jsPDF();
    const proj = projMap.get(te.projectId);
    const org = orgMap.get(te.organizationId);
    const disaster = disasterMap.get(te.disasterId);
    const approverCert = parseApproverCert(te.notes || '');
    const aName = approverCert?.name || te.approvedBy || '';
    const aTitle = approverCert?.title || '';
    const aDate = approverCert?.date || te.approvalDate || '';
    const isApproval = te.status === 'grantee_approved' || te.status === 'approved';
    const displayNotes = stripCertMarkers(te.notes);

    const LEFT = 25;
    const RIGHT_MARGIN = 25;
    const PAGE_WIDTH = 210 - LEFT - RIGHT_MARGIN;
    let y = 30;

    function checkPage(needed: number) {
      if (y + needed > 270) { pdfDoc.addPage(); y = 30; }
    }

    function drawLine(text: string, opts?: { bold?: boolean; size?: number; italic?: boolean }) {
      const sz = opts?.size ?? 11;
      pdfDoc.setFontSize(sz);
      pdfDoc.setFont('helvetica', opts?.bold ? 'bold' : opts?.italic ? 'italic' : 'normal');
      const wrapped = pdfDoc.splitTextToSize(text, PAGE_WIDTH);
      for (const wl of wrapped) {
        checkPage(sz * 0.5);
        pdfDoc.text(wl, LEFT, y);
        y += sz * 0.45;
      }
    }

    function blankLine(height = 4) { y += height; }

    // Date
    drawLine(`Date: ${aDate}`, { bold: true, size: 10 });
    blankLine(6);

    // From — find grantee org
    const granteeOrg = org?.orgRole === 'grantee' ? org
      : organizations.find(o => String(o.Rid) === org?.parentOrgId && o.orgRole === 'grantee') || null;
    const granteeOrgName = granteeOrg?.orgName || 'Grantee';
    const granteeAddr = [granteeOrg?.physicalAddress, [granteeOrg?.physicalCity, granteeOrg?.physicalState, granteeOrg?.physicalZip].filter(Boolean).join(', ')].filter(Boolean).join(', ');

    drawLine('From:', { bold: true, size: 10 });
    drawLine(`${aName}${aTitle ? ', ' + aTitle : ''}`, { size: 10 });
    drawLine(granteeOrgName, { size: 10 });
    if (granteeAddr) drawLine(granteeAddr, { size: 10 });
    blankLine(6);

    // To
    drawLine('To:', { bold: true, size: 10 });
    drawLine(org?.primaryContact || org?.orgName || '', { size: 10 });
    drawLine(org?.orgName || '', { size: 10 });
    const orgAddr = [org?.physicalAddress, [org?.physicalCity, org?.physicalState, org?.physicalZip].filter(Boolean).join(', ')].filter(Boolean).join(', ');
    if (orgAddr) drawLine(orgAddr, { size: 10 });
    blankLine(6);

    // RE
    const statusWord = isApproval ? 'Approval' : 'Denial';
    drawLine(`RE: Time Extension ${statusWord} — Project #${proj?.projectNumber || ''} (${proj?.projectTitle || ''})`, { bold: true, size: 11 });
    drawLine(`Disaster: ${disaster?.disasterNumber || ''} — ${disaster?.disasterName || ''}`, { size: 10 });
    blankLine(6);

    // Body
    if (isApproval) {
      drawLine('This letter serves to confirm that your request for a time extension for the above-referenced project has been approved under the grantee\'s authority per 44 CFR 206.204.', { size: 11 });
      blankLine(5);
      drawLine(`The new completion deadline is ${te.approvedNewDeadline || te.requestedNewDeadline || '—'}. The original deadline was ${te.currentDeadline || '—'}, and an extension of ${te.extensionMonths || '—'} months has been granted.`, { size: 11 });
      blankLine(5);
    } else {
      drawLine('After careful review, your request for a time extension for the above-referenced project has been denied.', { size: 11 });
      blankLine(5);
    }

    if (displayNotes) {
      drawLine(displayNotes, { size: 11 });
      blankLine(5);
    }

    drawLine('Please contact this office if you have questions regarding this decision.', { size: 11 });
    blankLine(8);

    // Signature
    drawLine('Sincerely,', { italic: true, size: 11 });
    blankLine(8);
    drawLine(`/s/ ${aName}`, { bold: true, size: 11 });
    if (aTitle) drawLine(aTitle, { size: 10 });
    drawLine(granteeOrgName, { size: 10 });
    blankLine(10);

    // Certification
    checkPage(10);
    drawLine('CERTIFICATION: I hereby certify that this decision has been made in accordance with 44 CFR 206.204.', { size: 9, italic: true });

    pdfDoc.save(`TE-Decision-${proj?.projectNumber || te.Rid}-${aDate || 'decision'}.pdf`);
  }

  // ══════════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ══════════════════════════════════════════════════════════════

  function renderDetailView() {
    if (!selected) return null;
    const proj = projMap.get(selected.projectId);
    const org = orgMap.get(selected.organizationId);
    const disaster = disasterMap.get(selected.disasterId);
    const fullLetter = letterToReadable(selected.letterContent) || selected.justification || '';
    const circumstances = selected.supportingCircumstances ? selected.supportingCircumstances.split(',').filter(Boolean) : [];

    // Determine which actions to show
    const canEditTE = !isViewer && (selected.status === 'draft' || selected.status === 'revision_needed');
    const canRetract = !isViewer && isApplicant && selected.status === 'submitted';
    const canReview = !isViewer && !isApplicant && selected.status === 'submitted';
    const canRecordFema = !isViewer && !isApplicant && selected.status === 'forwarded_to_fema';
    // Non-applicants who created a draft can also edit/submit/delete
    const isCreatorDraft = !isApplicant && !isViewer && (selected.status === 'draft' || selected.status === 'revision_needed');
    // Authority check: cumulative months including prior approved TEs for this project
    const extensionMonths = parseInt(selected.extensionMonths) || 0;
    const granteeAuthority = getGranteeAuthorityMonths(proj?.category || '');
    const priorApprovedMonths = timeExtensions
      .filter(t => t.projectId === selected.projectId && t.Rid !== selected.Rid
        && ['grantee_approved', 'approved'].includes(t.status))
      .reduce((sum, t) => sum + (parseInt(t.extensionMonths) || 0), 0);
    const cumulativeMonths = priorApprovedMonths + extensionMonths;
    const withinGranteeAuthority = cumulativeMonths <= granteeAuthority;
    // Extract certification data from notes if present
    const certMatch = parseApproverCert(selected.notes || '');
    const certName = certMatch?.name || '';
    const certTitle = certMatch?.title || '';
    const certDate = certMatch?.date || '';
    const displayNotes = stripCertMarkers(selected.notes);

    // Decision letter availability
    const hasDecisionLetter = ['grantee_approved', 'approved', 'denied'].includes(selected.status);

    return (
      <Stack gap="md">
        {/* Header row */}
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => { setSelected(null); setReviewDeadline(''); setReviewNotes(''); setShowSubmitCert(false); }}
              size="sm"
            >
              Back to Time Extensions
            </Button>
          </Group>
          <Badge color={STATUS_COLORS[selected.status] || '#939598'} size="lg" variant="filled">
            {STATUS_LABELS[selected.status] || selected.status}
          </Badge>
        </Group>

        <div>
          <Title order={3}>
            <IconClock size={22} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Time Extension: {proj?.projectNumber || '—'} — {proj?.projectTitle || 'Untitled'}
          </Title>
          <Text c="dimmed" mt={4}>Applicant: {org?.orgName || '—'}</Text>
        </div>

        {/* Two-column layout: details + letter */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {/* Left: Request Details */}
          <Paper withBorder p="md">
            <Title order={4} mb="sm">Request Details</Title>
            <Stack gap={6}>
              <FieldRow label="Project" value={`${proj?.projectNumber || '—'} — ${proj?.projectTitle || ''}`} />
              <FieldRow label="Category" value={proj?.category ? `${proj.category} — ${proj?.workType || ''}` : '—'} />
              <FieldRow label="Estimated Cost" value={proj?.estimatedCost ? `$${Number(proj.estimatedCost).toLocaleString()}` : '—'} />
              <FieldRow label="Completion" value={proj?.completionPctEstimate ? `${proj.completionPctEstimate}%` : '—'} />
              <Divider my={4} />
              <FieldRow label="Current Deadline" value={selected.currentDeadline || '—'} />
              <FieldRow label="Requested Deadline" value={selected.requestedNewDeadline || '—'} />
              <FieldRow label="This Extension" value={selected.extensionMonths ? `${selected.extensionMonths} months` : '—'} />
              <FieldRow label="Prior Approved" value={priorApprovedMonths > 0 ? `${priorApprovedMonths} months` : 'None'} />
              <FieldRow label="Cumulative Total" value={`${cumulativeMonths} of ${granteeAuthority} months (Cat ${proj?.category || '?'})`} />
              <Divider my={4} />
              <FieldRow label="Disaster" value={disaster ? `${disaster.disasterNumber} — ${disaster.disasterName}` : '—'} />
              {circumstances.length > 0 && (
                <>
                  <Text size="xs" c="dimmed" mt={4}>Reasons:</Text>
                  {circumstances.map((c, i) => (
                    <Text key={i} size="sm" pl="sm">&#8226; {c.trim()}</Text>
                  ))}
                </>
              )}
              <Divider my={4} />
              <FieldRow label="Created by" value={selected.createdBy || '—'} />
              <FieldRow label="Request date" value={selected.requestDate ? new Date(selected.requestDate).toLocaleDateString() : '—'} />
            </Stack>
          </Paper>

          {/* Right: Letter Preview + Downloads */}
          <Paper withBorder p="md">
            <Title order={4} mb="sm">Letter Preview</Title>
            {fullLetter ? (
              <Paper
                p="sm"
                bg="gray.0"
                style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12, maxHeight: 400, overflowY: 'auto' }}
              >
                {fullLetter}
              </Paper>
            ) : (
              <Text c="dimmed" size="sm" ta="center" py="xl">No letter content yet</Text>
            )}

            {/* Request Letter downloads */}
            <Text size="xs" fw={600} mt="md" mb={4}>Request Letter</Text>
            <Group gap="xs">
              <Button
                variant="light"
                color="var(--data-viz-650)"
                size="xs"
                leftSection={<IconFileTypeDocx size={14} />}
                onClick={() => downloadDocx(selected)}
                disabled={!fullLetter}
              >
                Download Word
              </Button>
              <Button
                variant="light"
                color="#F86A0B"
                size="xs"
                leftSection={<IconFileTypePdf size={14} />}
                onClick={() => downloadPdf(selected)}
                disabled={!fullLetter}
              >
                Download PDF
              </Button>
            </Group>

            {/* Decision Letter downloads */}
            {hasDecisionLetter && (
              <>
                <Text size="xs" fw={600} mt="md" mb={4}>Decision Letter</Text>
                <Group gap="xs">
                  <Button
                    variant="light"
                    color="#03AC9E"
                    size="xs"
                    leftSection={<IconFileTypeDocx size={14} />}
                    onClick={() => downloadDecisionDocx(selected)}
                  >
                    Download Word
                  </Button>
                  <Button
                    variant="light"
                    color="var(--color-error)"
                    size="xs"
                    leftSection={<IconFileTypePdf size={14} />}
                    onClick={() => downloadDecisionPdf(selected)}
                  >
                    Download PDF
                  </Button>
                </Group>
              </>
            )}

            {/* FEMA Package downloads (forwarding cover letter + original request) */}
            {(selected.status === 'forwarded_to_fema' || selected.status === 'approved') && (selected.notes || '').includes('|||FWD|||') && (
              <>
                <Text size="xs" fw={600} mt="md" mb={4}>FEMA Package (Cover Letter + Request)</Text>
                <Group gap="xs">
                  <Button variant="light" color="var(--data-viz-100)" size="xs" leftSection={<IconFileTypeDocx size={14} />} onClick={() => downloadFemaPackageDocx(selected)}>Download Word</Button>
                  <Button variant="light" color="var(--data-viz-100)" size="xs" leftSection={<IconFileTypePdf size={14} />} onClick={() => downloadFemaPackagePdf(selected)}>Download PDF</Button>
                </Group>
              </>
            )}
          </Paper>
        </SimpleGrid>

        {/* Reviewer feedback banner */}
        {selected.status === 'grantee_approved' && (
          <Alert color="var(--color-success)" icon={<IconCheck size={18} />} title="Grantee Approved">
            Approved by <strong>{certName || selected.approvedBy || 'grantee'}</strong>
            {certTitle && <>, {certTitle}</>} on {certDate || selected.approvalDate || '—'}.
            {selected.approvedNewDeadline && <> New deadline: <strong>{selected.approvedNewDeadline}</strong>.</>}
            {displayNotes && <Text size="sm" mt={4}>{displayNotes}</Text>}
            {certName && <Text size="xs" c="dimmed" mt={4} fs="italic">Certified: "I hereby certify that this time extension request has been reviewed and is approved under the grantee's authority per 44 CFR 206.204."</Text>}
          </Alert>
        )}
        {selected.status === 'forwarded_to_fema' && (
          <Alert color="var(--data-viz-100)" icon={<IconSend size={18} />} title="Forwarded to FEMA">
            Forwarded by {selected.approvedBy || 'grantee'} on {selected.approvalDate || '—'}.
            Cumulative extensions: {cumulativeMonths} months (prior: {priorApprovedMonths} + this: {extensionMonths}) exceeds grantee authority ({granteeAuthority} months for Cat {proj?.category || '?'}).
            Awaiting FEMA Regional Administrator decision.
            {displayNotes && <Text size="sm" mt={4}>{displayNotes}</Text>}
          </Alert>
        )}
        {selected.status === 'approved' && (
          <Alert color="#03AC9E" icon={<IconCheck size={18} />} title="FEMA Approved">
            FEMA approved on {selected.approvalDate || '—'}.
            {selected.approvedNewDeadline && <> New deadline: <strong>{selected.approvedNewDeadline}</strong>.</>}
            {displayNotes && <Text size="sm" mt={4}>{displayNotes}</Text>}
          </Alert>
        )}
        {selected.status === 'denied' && (
          <Alert color="var(--color-error)" icon={<IconX size={18} />} title="Denied">
            Denied by {selected.approvedBy || 'reviewer'} on {selected.approvalDate || '—'}.
            {displayNotes && <Text size="sm" mt={4}>{displayNotes}</Text>}
          </Alert>
        )}
        {selected.status === 'revision_needed' && (
          <Alert color="#F86A0B" icon={<IconArrowBack size={18} />} title="Revision Needed">
            Revision requested by {selected.approvedBy || 'reviewer'} on {selected.approvalDate || '—'}:
            {displayNotes && <Text size="sm" mt={4} fw={500}>{displayNotes}</Text>}
          </Alert>
        )}

        {/* Action buttons */}
        {!isViewer && (
          <Paper withBorder p="md">
            {/* Applicant actions */}
            {isApplicant && canEditTE && (
              <Stack gap="sm">
                <Group gap="sm">
                  <Button leftSection={<IconEdit size={16} />} variant="light" onClick={() => openEdit(selected)}>Edit</Button>
                  <Button leftSection={<IconSend size={16} />} color="var(--color-primary-700)" onClick={() => setShowSubmitCert(true)}>
                    {selected.status === 'revision_needed' ? 'Resubmit' : 'Submit'}
                  </Button>
                  <Button leftSection={<IconTrash size={16} />} variant="light" color="var(--color-error)" onClick={() => setDeleteTarget(selected)}>Delete</Button>
                </Group>
                {showSubmitCert && (
                  <Stack gap="xs" mt="sm">
                    <Text size="sm" fw={600}>Submitter Certification</Text>
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                      <TextInput label="Your Name" value={submitterName} onChange={e => setSubmitterName(e.target.value)} />
                      <TextInput label="Your Title" value={submitterTitle} onChange={e => setSubmitterTitle(e.target.value)} />
                    </SimpleGrid>
                    <Checkbox
                      label="I hereby certify that the information in this time extension request is true and accurate to the best of my knowledge."
                      checked={submitCertChecked}
                      onChange={e => setSubmitCertChecked(e.currentTarget.checked)}
                      size="sm"
                    />
                    <Button
                      color="var(--color-primary-700)"
                      leftSection={<IconSend size={16} />}
                      disabled={!submitterName || !submitCertChecked}
                      onClick={() => handleSubmitWithCert(selected, submitterName, submitterTitle)}
                    >
                      Confirm & Submit
                    </Button>
                  </Stack>
                )}
              </Stack>
            )}
            {canRetract && (
              <Group gap="sm">
                <Button leftSection={<IconArrowBack size={16} />} variant="light" color="#F86A0B" onClick={() => handleRetract(selected)}>
                  Retract to Draft
                </Button>
              </Group>
            )}

            {/* Non-applicant creator actions on draft */}
            {isCreatorDraft && !isApplicant && (
              <Stack gap="sm">
                <Group gap="sm">
                  <Button leftSection={<IconEdit size={16} />} variant="light" onClick={() => openEdit(selected)}>Edit</Button>
                  <Button leftSection={<IconSend size={16} />} color="var(--color-primary-700)" onClick={() => setShowSubmitCert(true)}>
                    {selected.status === 'revision_needed' ? 'Resubmit' : 'Submit'}
                  </Button>
                  <Button leftSection={<IconTrash size={16} />} variant="light" color="var(--color-error)" onClick={() => setDeleteTarget(selected)}>Delete</Button>
                </Group>
                {showSubmitCert && (
                  <Stack gap="xs" mt="sm">
                    <Text size="sm" fw={600}>Submitter Certification</Text>
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                      <TextInput label="Your Name" value={submitterName} onChange={e => setSubmitterName(e.target.value)} />
                      <TextInput label="Your Title" value={submitterTitle} onChange={e => setSubmitterTitle(e.target.value)} />
                    </SimpleGrid>
                    <Checkbox
                      label="I hereby certify that the information in this time extension request is true and accurate to the best of my knowledge."
                      checked={submitCertChecked}
                      onChange={e => setSubmitCertChecked(e.currentTarget.checked)}
                      size="sm"
                    />
                    <Button
                      color="var(--color-primary-700)"
                      leftSection={<IconSend size={16} />}
                      disabled={!submitterName || !submitCertChecked}
                      onClick={() => handleSubmitWithCert(selected, submitterName, submitterTitle)}
                    >
                      Confirm & Submit
                    </Button>
                  </Stack>
                )}
              </Stack>
            )}

            {/* Grantee review for submitted items */}
            {canReview && (
              <Stack gap="sm">
                <Text fw={600} size="sm">Grantee Review</Text>
                {withinGranteeAuthority ? (
                  <Alert color="var(--color-success)" variant="light" p="xs">
                    <Text size="sm">This {extensionMonths}-month extension is <strong>within grantee authority</strong> ({granteeAuthority} months for Category {proj?.category}).
                    {priorApprovedMonths > 0 && <> Prior approved: {priorApprovedMonths} mo. Cumulative: <strong>{cumulativeMonths} of {granteeAuthority} mo</strong>.</>}
                    {priorApprovedMonths === 0 && <> You can approve directly.</>}
                    </Text>
                  </Alert>
                ) : (
                  <Alert color="var(--data-viz-100)" variant="light" p="xs">
                    <Text size="sm">Cumulative extensions of <strong>{cumulativeMonths} months</strong> (prior: {priorApprovedMonths} + this: {extensionMonths}) <strong>exceeds grantee authority</strong> ({granteeAuthority} months for Category {proj?.category}). Must be forwarded to FEMA Regional Administrator.</Text>
                  </Alert>
                )}
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  {withinGranteeAuthority && (
                    <TextInput
                      label="Approved New Deadline"
                      description={`Auto-calculated: ${selected.currentDeadline || 'original deadline'} + ${selected.extensionMonths || 0} months`}
                      placeholder="YYYY-MM-DD"
                      value={reviewDeadline}
                      onChange={e => setReviewDeadline(e.target.value)}
                    />
                  )}
                  <Textarea
                    label="Review Notes"
                    placeholder="Notes for the applicant..."
                    value={reviewNotes}
                    onChange={e => setReviewNotes(e.target.value)}
                    rows={2}
                  />
                </SimpleGrid>
                {/* FEMA Forwarding form — only for beyond-authority cases */}
                {!withinGranteeAuthority && (
                  <Stack gap="xs">
                    <Divider />
                    <Text size="sm" fw={600}>FEMA Forwarding Letter</Text>
                    {recipients.length > 0 && (
                      <Select
                        label="FEMA Recipient"
                        placeholder="Select preset recipient or type below"
                        data={recipients.map((r, i) => ({ value: String(i), label: `${r.name} — ${r.org}` }))}
                        onChange={v => {
                          if (v !== null) {
                            const r = recipients[parseInt(v)];
                            if (r) { setFwdRecipientName(r.name); setFwdRecipientTitle(r.title); setFwdRecipientOrg(r.org); setFwdRecipientAddress(r.address); }
                          }
                        }}
                        clearable
                      />
                    )}
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                      <TextInput label="FEMA Recipient Name" placeholder="e.g., Regional Administrator" value={fwdRecipientName} onChange={e => setFwdRecipientName(e.target.value)} />
                      <TextInput label="Recipient Title" value={fwdRecipientTitle} onChange={e => setFwdRecipientTitle(e.target.value)} />
                      <TextInput label="Organization" value={fwdRecipientOrg} onChange={e => setFwdRecipientOrg(e.target.value)} />
                      <TextInput label="Address" value={fwdRecipientAddress} onChange={e => setFwdRecipientAddress(e.target.value)} />
                    </SimpleGrid>
                    <Select
                      label="Reason for Referral"
                      placeholder="Select reason"
                      data={FORWARDING_REASONS}
                      value={fwdReason}
                      onChange={v => setFwdReason(v || '')}
                    />
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                      <TextInput label="Your Name" placeholder="Full name" value={fwdName} onChange={e => setFwdName(e.target.value)} />
                      <TextInput label="Title" placeholder="e.g., Emergency Management Director" value={fwdTitle} onChange={e => setFwdTitle(e.target.value)} />
                    </SimpleGrid>
                    <Group justify="space-between" align="flex-end">
                      <Text size="xs" c="dimmed">Draft a cover letter to FEMA explaining why this request requires their approval.</Text>
                      <Button size="xs" variant="light" color="var(--data-viz-650)" leftSection={<IconRobot size={14} />} loading={fwdDrafting} onClick={handleDraftFwdLetter} disabled={!fwdReason}>
                        Draft with AI
                      </Button>
                    </Group>
                    <Textarea
                      label="Cover Letter Body"
                      placeholder="Explain the reason for forwarding and your recommendation..."
                      value={fwdLetterBody}
                      onChange={e => setFwdLetterBody(e.target.value)}
                      minRows={6}
                      autosize
                    />
                    <Checkbox
                      label="I certify that this time extension request has been reviewed and is being forwarded to FEMA for approval per 44 CFR 206.204."
                      checked={fwdCertChecked}
                      onChange={e => setFwdCertChecked(e.currentTarget.checked)}
                      size="sm"
                    />
                  </Stack>
                )}
                {withinGranteeAuthority && (
                  <Stack gap="xs">
                    <Text size="sm" fw={600}>Approver Certification</Text>
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                      <TextInput label="Approver Name" placeholder="Full name" value={approverName} onChange={e => setApproverName(e.target.value)} />
                      <TextInput label="Title" placeholder="e.g., Emergency Management Director" value={approverTitle} onChange={e => setApproverTitle(e.target.value)} />
                    </SimpleGrid>
                    <Checkbox
                      label="I hereby certify that this time extension request has been reviewed and is approved under the grantee's authority per 44 CFR 206.204."
                      checked={certChecked}
                      onChange={e => setCertChecked(e.currentTarget.checked)}
                      size="sm"
                    />
                  </Stack>
                )}
                <Group gap="sm">
                  {withinGranteeAuthority && (
                    <Button
                      color="var(--color-success)"
                      leftSection={<IconCheck size={16} />}
                      loading={reviewBusy}
                      disabled={!approverName || !certChecked}
                      onClick={() => handleGranteeApprove(selected, reviewDeadline, reviewNotes, approverName, approverTitle)}
                    >
                      Approve (Grantee Authority)
                    </Button>
                  )}
                  {!withinGranteeAuthority && (
                    <Button
                      color="var(--data-viz-100)"
                      leftSection={<IconSend size={16} />}
                      loading={reviewBusy}
                      disabled={!fwdName || !fwdCertChecked}
                      onClick={() => handleForwardToFema(selected, reviewNotes, fwdReason, fwdName, fwdTitle, fwdLetterBody)}
                    >
                      Forward to FEMA
                    </Button>
                  )}
                  <Button
                    color="#F86A0B"
                    variant="light"
                    leftSection={<IconArrowBack size={16} />}
                    loading={reviewBusy}
                    onClick={() => handleRequestRevision(selected, reviewNotes)}
                  >
                    Request Revision
                  </Button>
                  <Button
                    color="var(--color-error)"
                    variant="light"
                    leftSection={<IconX size={16} />}
                    loading={reviewBusy}
                    onClick={() => handleDeny(selected, reviewNotes)}
                  >
                    Deny
                  </Button>
                </Group>
              </Stack>
            )}

            {/* Record FEMA decision for forwarded items */}
            {canRecordFema && (
              <Stack gap="sm">
                <Text fw={600} size="sm">Record FEMA Decision</Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  <TextInput
                    label="FEMA Approved Deadline"
                    placeholder={selected.requestedNewDeadline || 'YYYY-MM-DD'}
                    value={reviewDeadline}
                    onChange={e => setReviewDeadline(e.target.value)}
                  />
                  <Textarea
                    label="Notes"
                    placeholder="FEMA decision reference..."
                    value={reviewNotes}
                    onChange={e => setReviewNotes(e.target.value)}
                    rows={2}
                  />
                </SimpleGrid>
                <Group gap="sm">
                  <Button
                    color="#03AC9E"
                    leftSection={<IconCheck size={16} />}
                    loading={reviewBusy}
                    onClick={() => handleFemaApprove(selected, reviewDeadline, reviewNotes)}
                  >
                    FEMA Approved
                  </Button>
                  <Button
                    color="var(--color-error)"
                    variant="light"
                    leftSection={<IconX size={16} />}
                    loading={reviewBusy}
                    onClick={() => handleDeny(selected, reviewNotes)}
                  >
                    FEMA Denied
                  </Button>
                </Group>
              </Stack>
            )}
          </Paper>
        )}
      </Stack>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // LIST VIEW
  // ══════════════════════════════════════════════════════════════

  const listView = (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3}>
          <IconClock size={24} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Time Extension Requests
        </Title>
        <Group gap="xs">
          <Button leftSection={<IconDownload size={16} />} variant="light" size="xs" onClick={() => {
            exportToCSV('pa-time-extensions.csv',
              ['Project #', 'Applicant', 'Current Deadline', 'Requested Deadline', 'Months', 'Status', 'Created'],
              filtered.map(te => {
                const p = projMap.get(te.projectId);
                const o = orgMap.get(te.organizationId);
                return [p?.projectNumber || '', o?.orgName || '', te.currentDeadline, te.requestedNewDeadline, te.extensionMonths, te.status, te.requestDate];
              })
            );
          }}>Export CSV</Button>
          {!isViewer && (
            <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>New TE Request</Button>
          )}
        </Group>
      </Group>

      <Group gap="sm">
        <TextInput
          placeholder="Search projects, orgs..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1 }}
          size="sm"
        />
        <Select
          placeholder="All statuses"
          data={[
            { value: 'draft', label: 'Draft' },
            { value: 'submitted', label: 'Submitted' },
            { value: 'grantee_approved', label: 'Grantee Approved' },
            { value: 'forwarded_to_fema', label: 'Forwarded to FEMA' },
            { value: 'approved', label: 'FEMA Approved' },
            { value: 'denied', label: 'Denied' },
            { value: 'revision_needed', label: 'Revision Needed' },
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
          clearable
          size="sm"
          style={{ width: 180 }}
        />
      </Group>

      <Paper withBorder>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
          <Table striped highlightOnHover>
            <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
              <Table.Tr>
                <Table.Th>Project</Table.Th>
                <Table.Th>Applicant</Table.Th>
                <Table.Th>Current Deadline</Table.Th>
                <Table.Th>Requested Deadline</Table.Th>
                <Table.Th>Extension</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Date</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7} style={{ textAlign: 'center', padding: 24 }}>
                    <Text c="dimmed">No time extension requests found</Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {filtered.map(te => {
                const proj2 = projMap.get(te.projectId);
                const org2 = orgMap.get(te.organizationId);
                return (
                  <Table.Tr
                    key={te.Rid}
                    onClick={() => { setSelected(te); setReviewNotes(''); setShowSubmitCert(false); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <Table.Td fw={500}>
                      {proj2?.projectNumber || '—'}
                      <br />
                      <Text size="xs" c="dimmed">{proj2?.projectTitle || ''}</Text>
                    </Table.Td>
                    <Table.Td>{org2?.orgName || '—'}</Table.Td>
                    <Table.Td>{te.currentDeadline || '—'}</Table.Td>
                    <Table.Td>{te.requestedNewDeadline || '—'}</Table.Td>
                    <Table.Td>{te.extensionMonths ? `${te.extensionMonths} mo` : '—'}</Table.Td>
                    <Table.Td>
                      <Badge color={STATUS_COLORS[te.status] || '#939598'} size="sm">
                        {STATUS_LABELS[te.status] || te.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{fmtDate(te.requestDate)}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </div>
      </Paper>

    </Stack>
  );

  // ══════════════════════════════════════════════════════════════
  // RENDER: detail view OR list view, plus modals always present
  // ══════════════════════════════════════════════════════════════

  return (
    <>
      {loadingTimeExtensions && timeExtensions.length === 0
        ? <Center py="xl"><Loader /></Center>
        : selected ? renderDetailView() : listView}

      {/* ── Create/Edit Modal ── */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} size="xl" title={editing ? 'Edit Time Extension Request' : 'New Time Extension Request'} closeOnClickOutside={false}>
        {/* Revision feedback alert at top of modal */}
        {editing && editing.status === 'revision_needed' && editing.notes && (
          <Alert color="#F86A0B" icon={<IconArrowBack size={18} />} mb="md" title="Revision Requested">
            {editing.approvedBy && <Text size="sm" c="dimmed" mb={4}>By {editing.approvedBy} on {fmtDate(editing.approvalDate)}</Text>}
            <Text size="sm" fw={500}>{editing.notes}</Text>
          </Alert>
        )}

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="details">Request Details</Tabs.Tab>
            <Tabs.Tab value="recipient">Recipient</Tabs.Tab>
            <Tabs.Tab value="letter">Letter</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="details" pt="md">
            <Stack gap="sm">
              <Select
                label="Project"
                placeholder="Select a project"
                data={availableProjects.map(p => ({ value: String(p.Rid), label: `${p.projectNumber} — ${p.projectTitle}` }))}
                value={form.projectId}
                onChange={v => v && handleProjectChange(v)}
                searchable
              />
              {/* Prior extensions warning */}
              {form.projectId && (() => {
                const modalProj = projMap.get(form.projectId);
                const modalAuthority = getGranteeAuthorityMonths(modalProj?.category || '');
                const modalPrior = timeExtensions
                  .filter(t => t.projectId === form.projectId
                    && (!editing || t.Rid !== editing.Rid)
                    && ['grantee_approved', 'forwarded_to_fema', 'approved'].includes(t.status))
                  .reduce((sum, t) => sum + (parseInt(t.extensionMonths) || 0), 0);
                const modalRequested = parseInt(form.extensionMonths) || 0;
                const modalCumulative = modalPrior + modalRequested;
                const overLimit = modalCumulative > modalAuthority;
                return (modalPrior > 0 || overLimit) ? (
                  <Alert color={overLimit ? '#F86A0B' : 'var(--color-primary-700)'} variant="light" p="xs">
                    <Text size="sm">
                      {modalPrior > 0 && <>This project has <strong>{modalPrior} months</strong> of prior approved extensions. </>}
                      {modalRequested > 0 && <>With this {modalRequested}-month request, cumulative total: <strong>{modalCumulative} of {modalAuthority} months</strong> (Cat {modalProj?.category}). </>}
                      {overLimit && <><strong>Exceeds grantee authority — will require FEMA approval.</strong></>}
                    </Text>
                  </Alert>
                ) : null;
              })()}
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                <TextInput label="Current Deadline" value={form.currentDeadline} readOnly />
                <Select
                  label="Extension Length"
                  data={Array.from({ length: 36 }, (_, i) => ({ value: String(i + 1), label: `${i + 1} month${i > 0 ? 's' : ''}` }))}
                  value={form.extensionMonths}
                  onChange={v => v && handleMonthsChange(v)}
                />
                <TextInput label="Requested New Deadline" value={form.requestedNewDeadline} readOnly />
              </SimpleGrid>
              <Text size="sm" fw={500} mt="xs">Supporting Circumstances</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                {TIME_EXTENSION_REASONS.map(reason => (
                  <Checkbox
                    key={reason}
                    label={reason}
                    size="xs"
                    checked={form.supportingCircumstances.includes(reason)}
                    onChange={e => {
                      const checked = e.currentTarget.checked;
                      setForm(f => ({
                        ...f,
                        supportingCircumstances: checked
                          ? [...f.supportingCircumstances, reason]
                          : f.supportingCircumstances.filter(r => r !== reason),
                      }));
                    }}
                  />
                ))}
              </SimpleGrid>
              <Textarea label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="recipient" pt="md">
            <Stack gap="sm">
              {recipients.length > 0 && (
                <Select
                  label="Select a Preset Recipient"
                  placeholder="Choose or type manually below"
                  data={recipients.map((r, i) => ({ value: String(i), label: `${r.name} — ${r.org}` }))}
                  onChange={v => {
                    if (v !== null) {
                      const r = recipients[parseInt(v)];
                      if (r) setForm(f => ({ ...f, recipientName: r.name, recipientTitle: r.title, recipientOrg: r.org, recipientAddress: r.address }));
                    }
                  }}
                  clearable
                  mb="sm"
                />
              )}
              {!isApplicant && (
                <Group justify="flex-end">
                  <Button
                    variant="subtle"
                    size="xs"
                    leftSection={<IconSettings size={14} />}
                    onClick={openRecipientsEditor}
                  >
                    Manage Recipients
                  </Button>
                </Group>
              )}
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <TextInput label="Recipient Name" value={form.recipientName} onChange={e => setForm(f => ({ ...f, recipientName: e.target.value }))} />
                <TextInput label="Recipient Title" value={form.recipientTitle} onChange={e => setForm(f => ({ ...f, recipientTitle: e.target.value }))} />
                <TextInput label="Recipient Organization" value={form.recipientOrg} onChange={e => setForm(f => ({ ...f, recipientOrg: e.target.value }))} />
                <TextInput label="Recipient Address" value={form.recipientAddress} onChange={e => setForm(f => ({ ...f, recipientAddress: e.target.value }))} />
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="letter" pt="md">
            <Stack gap="sm">
              <Group justify="flex-end">
                <Button leftSection={<IconRobot size={16} />} variant="light" color="var(--data-viz-650)" loading={drafting} onClick={handleDraftWithAI} disabled={!form.projectId}>
                  Draft with AI
                </Button>
              </Group>
              <Textarea label="Letter Header (Date, Addresses, Subject)" value={form.letterHeader} onChange={e => setForm(f => ({ ...f, letterHeader: e.target.value }))} minRows={8} autosize styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }} />
              <Textarea label="Letter Body (Justification)" value={form.letterBody} onChange={e => setForm(f => ({ ...f, letterBody: e.target.value, justification: e.target.value }))} minRows={10} autosize />
              <Textarea label="Closing & Signature Block" value={form.letterClosing} onChange={e => setForm(f => ({ ...f, letterClosing: e.target.value }))} minRows={5} autosize styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }} />
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button loading={saving} onClick={handleSave} disabled={!form.projectId}>Save</Button>
        </Group>
      </Modal>

      {/* ── Delete Confirm ── */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} size="sm" title="Delete Time Extension Request" closeOnClickOutside={false}>
        <Text>Are you sure you want to delete this time extension request?</Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="var(--color-error)" onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>

      {/* ── Manage Recipients Modal ── */}
      <Modal opened={showRecipientsEditor} onClose={() => setShowRecipientsEditor(false)} size="lg" title="Manage TE Recipients" closeOnClickOutside={false}>
        <Stack gap="sm">
          <Text size="sm" c="dimmed">Add preset recipients that can be quickly selected when creating time extension requests.</Text>
          {recipientsEditing.map((r, i) => (
            <Paper key={i} withBorder p="xs">
              <Group gap="xs" align="flex-end">
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" style={{ flex: 1 }}>
                  <TextInput
                    label="Name"
                    size="xs"
                    value={r.name}
                    onChange={e => {
                      const updated = [...recipientsEditing];
                      updated[i] = { ...updated[i], name: e.target.value };
                      setRecipientsEditing(updated);
                    }}
                  />
                  <TextInput
                    label="Title"
                    size="xs"
                    value={r.title}
                    onChange={e => {
                      const updated = [...recipientsEditing];
                      updated[i] = { ...updated[i], title: e.target.value };
                      setRecipientsEditing(updated);
                    }}
                  />
                  <TextInput
                    label="Organization"
                    size="xs"
                    value={r.org}
                    onChange={e => {
                      const updated = [...recipientsEditing];
                      updated[i] = { ...updated[i], org: e.target.value };
                      setRecipientsEditing(updated);
                    }}
                  />
                  <TextInput
                    label="Address"
                    size="xs"
                    value={r.address}
                    onChange={e => {
                      const updated = [...recipientsEditing];
                      updated[i] = { ...updated[i], address: e.target.value };
                      setRecipientsEditing(updated);
                    }}
                  />
                </SimpleGrid>
                <ActionIcon
                  color="var(--color-error)"
                  variant="light"
                  size="sm"
                  onClick={() => setRecipientsEditing(recipientsEditing.filter((_, idx) => idx !== i))}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
            </Paper>
          ))}
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => setRecipientsEditing([...recipientsEditing, { name: '', title: '', org: '', address: '' }])}
          >
            Add Recipient
          </Button>
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setShowRecipientsEditor(false)}>Cancel</Button>
            <Button loading={recipientsSaving} onClick={saveRecipients}>Save Recipients</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

// ── Reusable field row ──────────────────────────────────────

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <Group gap={8} wrap="nowrap">
      <Text size="xs" c="dimmed" w={130} style={{ flexShrink: 0 }}>{label}</Text>
      <Text size="sm" fw={500}>{value}</Text>
    </Group>
  );
}
